/**
 * ChatRow — riga di conversazione condivisa tra Inbox ("Da vedere") e
 * la schermata Conversazioni. Estratta da InboxScreen per riuso.
 *
 * Esporta anche due helper puri:
 *   - formatTime(ts): etichetta ora/data compatta
 *   - isNewChat(c): true se c'è attività più recente dell'ultima apertura
 */

import { useState } from 'react';

export function formatTime(ts) {
  if (!ts) return '';
  const ms = ts?.toMillis ? ts.toMillis() : (typeof ts === 'number' ? ts : Date.now());
  const date = new Date(ms);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

// "Da leggere" = c'è un messaggio IN ENTRATA (mandato dall'altro) più recente
// dell'ultima volta che ho aperto la chat. Un mio messaggio in uscita NON deve
// mai marcare la chat come non letta — anche se il `seen` (orario client) resta
// indietro rispetto a `lastMessageAt` (orario server) per skew o invia-e-esci.
// `seen` viene scritto da ChatScreen all'apertura/aggiornamento.
export function isNewChat(c) {
  try {
    const seen = parseInt(localStorage.getItem(`nullum.chatSeen.${c.id}`) || '0', 10) || 0;
    if ((c.lastMessageAt || 0) <= seen) return false;
    // L'ultimo messaggio è in entrata? (lo ha mandato l'altro partecipante)
    if (c.lastSenderSigilLower) return c.lastSenderSigilLower === (c.otherSigilLower || null);
    if (c.lastSenderUid && c.otherUid) return c.lastSenderUid === c.otherUid;
    // Doc legacy senza info mittente: comportamento storico (solo timestamp).
    return true;
  } catch { return false; }
}

function RowMenu({ onHide, onDeleteBoth, sigil }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0">
      <button
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="text-ink-dim hover:text-ink text-[18px] px-2 py-1"
        title="opzioni"
      >⋯</button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-10 bg-black border border-ink-dim/60 rounded-lg overflow-hidden min-w-[200px]">
          <button
            onClick={(e) => {
              e.stopPropagation(); setOpen(false);
              if (confirm(`Rimuovere la chat con @${sigil} dalla tua lista? L'altro continua a vederla. La puoi ritrovare aprendone una nuova con lo stesso sigil.`)) {
                onHide();
              }
            }}
            className="block w-full text-left px-3 py-2 text-[14px] text-ink-dim hover:bg-ink/10 hover:text-ink"
          >− Rimuovi solo per me</button>
          <button
            onClick={(e) => {
              e.stopPropagation(); setOpen(false);
              if (confirm(`Eliminare la chat con @${sigil}?\n\nVengono annullati (per entrambi) SOLO i tuoi messaggi che @${sigil} non ha ancora letto. Quelli già letti restano da lui e spariranno col loro timer. La chat sparisce comunque dalla tua lista.`)) {
                onDeleteBoth();
              }
            }}
            className="block w-full text-left px-3 py-2 text-[14px] text-red-400 hover:bg-red-500/10"
          >✕ Elimina (annulla i non letti)</button>
        </div>
      )}
    </div>
  );
}

export function ChatRow({ chat, isNew, onClick, onHide, onDeleteBoth, selectMode, selected, onToggleSelect }) {
  const sigil = chat.otherSigil || '???';
  const pending = chat.status === 'pending';
  const incoming = pending && chat.initiator !== chat.callerUid;
  return (
    <div className={`w-full border rounded-xl p-3 mb-2.5 transition-colors flex items-center gap-2 ${selected ? 'border-ink bg-ink/10' : 'border-ink-dim/50 bg-ink/[0.015] hover:border-ink-dim hover:bg-ink/[0.04]'}`}>
      {selectMode && (
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onToggleSelect(chat.id)}
          className="w-4 h-4 accent-green-500 shrink-0"
        />
      )}
      <button onClick={selectMode ? () => onToggleSelect(chat.id) : onClick} className="flex-1 text-left min-w-0">
        <div className="flex items-start justify-between text-[15px]">
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              {isNew && <span className="w-2 h-2 rounded-full bg-ink shadow-glow shrink-0" title="nuovi messaggi" />}
              <span className={`truncate ${isNew ? 'text-glow font-semibold' : ''} ${pending ? 'text-amber' : 'text-ink'}`}>
                @{sigil}
              </span>
              {incoming && (
                <span className="text-[11px] text-amber border border-amber rounded-full px-2 py-0.5 shrink-0">
                  RICHIESTA
                </span>
              )}
              {pending && !incoming && (
                <span className="text-[11px] text-ink-faint border border-ink-faint rounded-full px-2 py-0.5 shrink-0">
                  IN ATTESA DI AUT.
                </span>
              )}
            </div>
            {chat.mineIsDerived && chat.mySigilInChat && (
              <div className="text-[11px] text-ink-dim mt-0.5">
                come @{chat.mySigilInChat}
              </div>
            )}
          </div>
          <span className="text-ink-faint text-[13px] shrink-0 ml-2">
            {formatTime(chat.lastMessageAt)}
          </span>
        </div>
      </button>
      {!selectMode && <RowMenu onHide={onHide} onDeleteBoth={onDeleteBoth} sigil={sigil} />}
    </div>
  );
}
